import { Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { catchError, debounceTime, map, merge, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { SubDistrictDto } from '../../../../generated-model/models';
import { SubdistrictService } from '../../../views/master/subdistrict/subdistrict-service';

/** Page size asked of the service, and the hard cap on how many suggestions are shown. */
const SUGGESTION_LIMIT = 10;
const DEBOUNCE_MS = 300;

@Component({
  selector: 'app-subdistrict-autocomplete',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './subdistrict-autocomplete.html',
  styleUrl: './subdistrict-autocomplete.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SubdistrictAutocompleteComponent),
      multi: true,
    },
  ],
})
export class SubdistrictAutocompleteComponent implements ControlValueAccessor, OnChanges {
  @Input() placeholder = 'พิมพ์ค้นหารหัสหรือชื่อตำบล/แขวง';
  @Input() initialDisplayText: string | null = null;
  @Input() bindValue: 'code' | null = 'code';
  @Output() selected = new EventEmitter<SubDistrictDto>();

  @ViewChild('searchInput', { static: true }) searchInput?: ElementRef<HTMLInputElement>;

  searchText = '';
  suggestions: SubDistrictDto[] = [];
  loading = false;
  open = false;
  disabled = false;

  private selectedValue: SubDistrictDto | string | null = null;
  private onChange: (value: string | SubDistrictDto | null) => void = () => {};
  private onTouched: () => void = () => {};

  /** Keystrokes — debounced so one request is sent per pause, not per character. */
  private readonly typed$ = new Subject<string>();
  /** Dropdown opened by focus/caret — searched right away. */
  private readonly requested$ = new Subject<string>();

  constructor(private subdistrictService: SubdistrictService) {
    merge(this.typed$.pipe(debounceTime(DEBOUNCE_MS)), this.requested$)
      .pipe(
        tap(() => (this.loading = true)),
        // switchMap drops the response of a superseded term, so a slow early
        // request can never overwrite the suggestions of a later one.
        switchMap((term) => this.fetchSuggestions(term)),
        takeUntilDestroyed(),
      )
      .subscribe((subdistricts) => {
        this.suggestions = subdistricts;
        this.open = subdistricts.length > 0;
        this.loading = false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The form control can be written before `initialDisplayText` arrives; without
    // this the edit case would keep showing the raw code (or nothing) instead of a name.
    if (changes['initialDisplayText'] && this.selectedValue != null && this.initialDisplayText) {
      this.searchText = this.initialDisplayText;
    }
  }

  writeValue(value: string | SubDistrictDto | null): void {
    this.selectedValue = value;

    if (value == null) {
      this.searchText = '';
      this.suggestions = [];
      return;
    }

    if (typeof value === 'object') {
      this.searchText = this.formatSubdistrict(value);
      return;
    }

    if (this.initialDisplayText) {
      this.searchText = this.initialDisplayText;
      return;
    }

    this.searchText = `${value}`;
  }

  registerOnChange(fn: (value: string | SubDistrictDto | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.open && !target.closest('.subdistrict-autocomplete')) {
      this.open = false;
      this.onTouched();
    }
  }

  onInputChange(): void {
    const term = this.searchText.trim();
    this.selectedValue = null;
    this.onChange(null);

    if (!term) {
      this.suggestions = [];
      this.open = false;
      this.loading = false;
      return;
    }

    this.typed$.next(term);
  }

  onFocus(): void {
    if (!this.searchText.trim() && !this.open) {
      this.requested$.next('');
    }
  }

  toggleOpen(): void {
    if (this.disabled) {
      return;
    }
    if (this.open) {
      this.open = false;
      return;
    }
    this.requested$.next(this.searchText.trim());
  }

  selectSubdistrict(item: SubDistrictDto): void {
    this.selectedValue = item;
    this.searchText = this.formatSubdistrict(item);
    this.suggestions = [];
    this.open = false;
    this.selected.emit(item);

    if (this.bindValue === null) {
      this.onChange(item);
    } else {
      this.onChange(item.code ?? null);
    }
    this.onTouched();
  }

  handleBlur(): void {
    // Closing is owned by onClickOutside/selectSubdistrict; a blur timeout here would
    // race the caret click and immediately re-close the dropdown it just opened.
  }

  trackBySubdistrictId(_: number, item: SubDistrictDto): number {
    return item.id;
  }

  private fetchSuggestions(term: string): Observable<SubDistrictDto[]> {
    return this.subdistrictService.getAllSubdistricts(0, SUGGESTION_LIMIT, term).pipe(
      map((page) => {
        const list = page?.content ?? [];
        return term ? list : list.slice(0, SUGGESTION_LIMIT);
      }),
      catchError(() => of([])),
    );
  }

  private formatSubdistrict(item: SubDistrictDto): string {
    return `${item.code ?? '-'} - ${item.title ?? ''}`.trim();
  }
}
